// 参加者一覧から設定するマイカラーを、認証利用者ごとに localStorage へ保存する。
// ペン・図形などのカスタム色とは名前空間を分け、相互に上書きしない。

import { isViewerColor } from "@refinement-canvas/domain";
import { addColorToList, removeColorFromList, updateColorInList } from "./custom-color-list";
import { isValidHexColor, normalizeHexColor } from "./hex-color";

export const VIEWER_COLOR_PREFERENCE_STORAGE_KEY = "refinement-canvas:viewer-color-preference:v1";

export const VIEWER_PRESET_COLORS = [
  "#1e63d4",
  "#d22b2b",
  "#2da55a",
  "#9b59b6",
  "#e67e22",
  "#16a085",
] as const;

/** 既存の各色パレットと同じカスタム色上限。 */
export const VIEWER_CUSTOM_COLOR_LIMIT = 12;

const FALLBACK_VIEWER_COLOR = "#1a1a2e";
const MAX_USER_COLOR_ENTRIES = 100;
const MAX_USER_ID_LENGTH = 160;
const MAX_STORED_JSON_LENGTH = 64 * 1024;

export interface ViewerColorStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface ViewerColorPreferenceState {
  readonly colors: Readonly<Record<string, string>>;
  readonly customColors: readonly string[];
}

export interface ViewerCustomColorUpdateResult {
  readonly colors: readonly string[];
  readonly applied: boolean;
  readonly saved: boolean;
}

const EMPTY_STATE: ViewerColorPreferenceState = { colors: {}, customColors: [] };

/** 変更前と同じアルゴリズム・パレット順で、未設定利用者の色を決める。 */
export function pickViewerColor(userId: string): string {
  let hash = 0;
  for (let index = 0; index < userId.length; index += 1) {
    hash = (hash * 31 + userId.charCodeAt(index)) >>> 0;
  }
  return VIEWER_PRESET_COLORS[hash % VIEWER_PRESET_COLORS.length] ?? FALLBACK_VIEWER_COLOR;
}

/** 保存済み色を読み、欠落・破損・読込失敗時は従来の決定論的な色へ戻す。 */
export function loadViewerColor(
  userId: string,
  storage: ViewerColorStorage = window.localStorage
): string {
  if (!isUsableUserId(userId)) return pickViewerColor(userId);
  const read = readPreferenceState(storage);
  if (!read.ok) return pickViewerColor(userId);
  const candidate = Object.hasOwn(read.state.colors, userId)
    ? Reflect.get(read.state.colors, userId)
    : undefined;
  return isViewerColor(candidate) ? normalizeHexColor(candidate) : pickViewerColor(userId);
}

/** 正規化した6桁 hexだけを利用者別に保存し、保存成否を返す。 */
export function saveViewerColor(
  userId: string,
  color: string,
  storage: ViewerColorStorage = window.localStorage
): boolean {
  if (!isUsableUserId(userId) || !isValidHexColor(color)) return false;
  const normalized = normalizeHexColor(color);
  if (!isViewerColor(normalized)) return false;
  const read = readPreferenceState(storage);
  if (!read.ok) return false;

  const entries = Object.entries(read.state.colors).filter(([key]) => key !== userId);
  const boundedEntries = entries.slice(-(MAX_USER_COLOR_ENTRIES - 1));
  const next: ViewerColorPreferenceState = {
    colors: Object.fromEntries([...boundedEntries, [userId, normalized]]),
    customColors: read.state.customColors,
  };
  return writePreferenceState(storage, next);
}

export function loadViewerCustomColors(
  storage: ViewerColorStorage = window.localStorage
): readonly string[] {
  const read = readPreferenceState(storage);
  return read.ok ? read.state.customColors : [];
}

export function addViewerCustomColor(
  color: string,
  storage: ViewerColorStorage = window.localStorage
): ViewerCustomColorUpdateResult {
  const read = readPreferenceState(storage);
  if (!read.ok) return { colors: [], applied: false, saved: false };
  const colors = addColorToList(
    read.state.customColors,
    color,
    VIEWER_PRESET_COLORS,
    VIEWER_CUSTOM_COLOR_LIMIT
  );
  if (colors === read.state.customColors) return { colors, applied: false, saved: true };
  const saved = writePreferenceState(storage, { ...read.state, customColors: colors });
  return { colors: saved ? colors : read.state.customColors, applied: saved, saved };
}

export function updateViewerCustomColor(
  previous: string,
  next: string,
  storage: ViewerColorStorage = window.localStorage
): ViewerCustomColorUpdateResult {
  const read = readPreferenceState(storage);
  if (!read.ok) return { colors: [], applied: false, saved: false };
  const updated = updateColorInList(read.state.customColors, previous, next, VIEWER_PRESET_COLORS);
  if (!updated.applied) return { colors: updated.colors, applied: false, saved: true };
  const saved = writePreferenceState(storage, { ...read.state, customColors: updated.colors });
  return {
    colors: saved ? updated.colors : read.state.customColors,
    applied: saved,
    saved,
  };
}

export function removeViewerCustomColor(
  color: string,
  storage: ViewerColorStorage = window.localStorage
): ViewerCustomColorUpdateResult {
  const read = readPreferenceState(storage);
  if (!read.ok) return { colors: [], applied: false, saved: false };
  const colors = removeColorFromList(read.state.customColors, color);
  if (colors === read.state.customColors) return { colors, applied: false, saved: true };
  const saved = writePreferenceState(storage, { ...read.state, customColors: colors });
  return { colors: saved ? colors : read.state.customColors, applied: saved, saved };
}

function isUsableUserId(userId: string): boolean {
  return userId.length > 0 && userId.length <= MAX_USER_ID_LENGTH;
}

function readPreferenceState(
  storage: ViewerColorStorage
): { readonly ok: true; readonly state: ViewerColorPreferenceState } | { readonly ok: false } {
  try {
    const raw = storage.getItem(VIEWER_COLOR_PREFERENCE_STORAGE_KEY);
    if (raw === null) return { ok: true, state: EMPTY_STATE };
    if (raw.length > MAX_STORED_JSON_LENGTH) return { ok: true, state: EMPTY_STATE };
    return { ok: true, state: normalizePreferenceState(JSON.parse(raw) as unknown) };
  } catch {
    return { ok: false };
  }
}

function normalizePreferenceState(value: unknown): ViewerColorPreferenceState {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return EMPTY_STATE;
  const record = value as Record<string, unknown>;
  const colorEntries: [string, string][] = [];
  if (
    typeof record.colors === "object" &&
    record.colors !== null &&
    !Array.isArray(record.colors)
  ) {
    for (const [userId, color] of Object.entries(record.colors)) {
      if (isUsableUserId(userId) && isViewerColor(color)) {
        colorEntries.push([userId, normalizeHexColor(color)]);
      }
      if (colorEntries.length >= MAX_USER_COLOR_ENTRIES) break;
    }
  }

  const customColors: string[] = [];
  if (Array.isArray(record.customColors)) {
    for (const color of record.customColors) {
      if (isViewerColor(color)) {
        const normalized = normalizeHexColor(color);
        if (
          !customColors.includes(normalized) &&
          !VIEWER_PRESET_COLORS.some((preset) => preset === normalized)
        ) {
          customColors.push(normalized);
        }
      }
      if (customColors.length >= VIEWER_CUSTOM_COLOR_LIMIT) break;
    }
  }
  return { colors: Object.fromEntries(colorEntries), customColors };
}

function writePreferenceState(
  storage: ViewerColorStorage,
  state: ViewerColorPreferenceState
): boolean {
  try {
    storage.setItem(VIEWER_COLOR_PREFERENCE_STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}
